/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useCallback, useRef } from "react";

import { useLazyQuery } from "@apollo/client";
import {
  AssignmentLate as AssignmentLateIcon,
  FilterAlt,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  useTheme,
  useMediaQuery,
  TablePagination,
  TableContainer,
  Stack,
  IconButton,
  Box,
  Button,
} from "@mui/material";

import PropTypes from "prop-types";
import { format } from "date-fns";
import { useModal } from "../../context";

import { useLocalStorage, useEventListener } from "../../hooks";
import equals from "../../utils/equals";
import omitBy from "../../utils/omitBy";
import Loader from "../Loader";

import Table from "./Table";
import MobileTable from "../ReportsTable/MobileTable";
import AccordionItem from "../AccordionItem";
import AccordionDetailsItem from "../AccordionDetailsItem";
import BoxLoader from "./BoxLoader/BoxLoader";
import checkMobile from "../../utils/checkForMobile";

// THIS IS THE ONE WE ARE USING FOR MAIN TABLES
const DetailedDataTable = ({
  columns,
  query,
  dataExtractor,
  totalExtractor,
  searchTransformer,
  defaultSorting = [],
  onRowClick = () => { },
  onCellClick = () => { },
  fetchPolicy = "cache-and-network",
  where: initialWhere = {},
  getIsRowSelected = () => false,
  enableRowSelection = true,
  FilterComponent = null,
  ActionComponent = null,
  ViewsComponent = null,
  opVarStorageKey = null,
  location = "",
  fullWidth = false,
  isModalTable = false,
  aggregateName = "",
  name = "",
}) => {
  const { setModalOpen } = useModal();
  const [currentPage, setCurrentPage] = useState(0);
  const [sorting, setSorting] = useState(defaultSorting || []);
  const [where, setWhere] = useState(initialWhere);
  const baseWhereRef = useRef(initialWhere);
  const uiWhereRef = useRef(null);
  // const [search, setSearch] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const isMobile = checkMobile();

  const [runQuery, { data, loading }] = useLazyQuery(query, {
    fetchPolicy,
  });

  const [defaultPageSize, setDefaultPageSize] = useLocalStorage(
    "DEFAULT_PAGE_SIZE",
    10
  );

  // const [debouncedSearch] = useDebouncedValue(search, 1000);
  const isSomeRowsSelected = !!Object.keys(rowSelection).length;

  // Apply default filters on first load
  const appliedDefaultYearRef = useRef(false);

  const executeQuery = useCallback(() => {
    console.log("this is where", where)
    
    // Separate client-side sorting (assignedTo) from server-side sorting
    const serverSideSorting = sorting.filter(sort => sort.id !== "assignedTo");
    const clientSideSorting = sorting.filter(sort => sort.id === "assignedTo");
    
    const variables = {
      where: omitBy(where, (x, y) => !x || y === "overdue"),
      orderBy: serverSideSorting.map((x) => {
        const sortDirection = x.desc ? "desc" : "asc";
        if (x.id === "subject") {
          return {
            user: { firstName: sortDirection },
          };
        }
        if (x.id === "signer") {
          return {
            signerName: sortDirection,
          };
        }
        return {
          [x.id]: sortDirection,
        };
      }),
      take: isMobile ? 10000 : defaultPageSize,
      skip: currentPage * defaultPageSize,
      sectionsOrderBy: [{ order: "asc" }],
      questionsOrderBy: [{ order: "asc" }],
    };
    if (opVarStorageKey) {
      window.sessionStorage.setItem(opVarStorageKey, JSON.stringify(variables));
    }
    runQuery({
      variables,
    });
  }, [
    where,
    sorting,
    defaultPageSize,
    currentPage,
    isMobile,
    opVarStorageKey,
    runQuery,
  ]);

  useEffect(() => {
    console.log("🔄 Executing query");
    executeQuery();
  }, [executeQuery]);

  // On first page load for reviews, default the Year filter to the current year
  useEffect(() => {
    if (appliedDefaultYearRef.current) return;
    const hasInitialWhere = initialWhere && Object.keys(initialWhere).length > 0;
    if (location === "reviews" && !hasInitialWhere) {
      appliedDefaultYearRef.current = true;
      handleFilterComplete({ year: String(new Date().getFullYear()) });
    }
  }, [location, initialWhere]);

  const handleChangePage = (e, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setDefaultPageSize(parseInt(e.target.value, 10));
    setCurrentPage(0);
  };

  const handleSortingChange = (fn) => {
    const nextSort = fn();
    if (nextSort[0].id !== "overdue") {
      setSorting(nextSort);
      setCurrentPage(0);
    }
  };

  const handleRowSelectionChange = (result) => {
    setRowSelection(result);
  };

  const [result, setResult] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const nextResult = dataExtractor(data);
    const nextTotal = totalExtractor(data?.[aggregateName]?._count?._all);
    
    if (nextResult) {
      // Apply client-side sorting for "assignedTo" column
      const assignedToSort = sorting.find(sort => sort.id === "assignedTo");
      
      if (assignedToSort) {
        const sortedResult = [...nextResult].sort((a, b) => {
          // Get assigned users for both items using the same logic as the column
          const getAssignedUsers = (item) => {
            const assignedUsers = item?.followups
              ?.filter(followup => followup.assignedToUser)
              ?.map(followup => `${followup.assignedToUser.firstName} ${followup.assignedToUser.lastName}`)
              ?.filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
            return assignedUsers?.length > 0 ? assignedUsers.join(", ") : "Not assigned";
          };
          
          const aAssigned = getAssignedUsers(a);
          const bAssigned = getAssignedUsers(b);
          
          // Sort "Not assigned" to the end regardless of sort direction
          if (aAssigned === "Not assigned" && bAssigned !== "Not assigned") {
            return 1;
          }
          if (bAssigned === "Not assigned" && aAssigned !== "Not assigned") {
            return -1;
          }
          if (aAssigned === "Not assigned" && bAssigned === "Not assigned") {
            return 0;
          }
          
          // Normal string comparison for assigned names
          const comparison = aAssigned.localeCompare(bAssigned);
          return assignedToSort.desc ? -comparison : comparison;
        });
        
        setResult(sortedResult);
      } else {
        setResult(nextResult);
      }
    }
    
    if (nextTotal) {
      setTotal(nextTotal);
    }
  }, [data, dataExtractor, totalExtractor, aggregateName, sorting]);

  // useEffect(() => {
  //   const nextWhere = searchTransformer?.(where, debouncedSearch);
  //   const isWhereEqual = equals(where, nextWhere);
  //   if (!isWhereEqual) {
  //     // setWhere(nextWhere);
  //     setCurrentPage(0);
  //   }
  // }, [debouncedSearch, searchTransformer, where]);

  useEventListener("keydown", (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setCurrentPage((prev) => Math.min(prev + 1, total / defaultPageSize - 1));
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setCurrentPage((prev) => Math.max(prev - 1, 0));
    }
  });

  const handleFilterComplete = (nextWhere) => {
    // Preserve raw UI values so reopening the filter shows exactly what user entered
    uiWhereRef.current = { ...nextWhere };
    // Build special name filters first and remove custom keys so they don't get transformed below
    const andConditions = [];
    const getString = (v) => (v && typeof v === "object" && typeof v.equals === "string" ? v.equals : v);

    if (nextWhere?.authorFirst || nextWhere?.authorLast) {
      const first = getString(nextWhere.authorFirst);
      const last = getString(nextWhere.authorLast);
      if (first) andConditions.push({ formAuthor: { contains: first, mode: "insensitive" } });
      if (last) andConditions.push({ formAuthor: { contains: last, mode: "insensitive" } });
      delete nextWhere.authorFirst;
      delete nextWhere.authorLast;
    }

    if (nextWhere?.ownerFirst || nextWhere?.ownerLast) {
      const first = getString(nextWhere.ownerFirst);
      const last = getString(nextWhere.ownerLast);
      if (first) andConditions.push({ owner: { contains: first, mode: "insensitive" } });
      if (last) andConditions.push({ owner: { contains: last, mode: "insensitive" } });
      delete nextWhere.ownerFirst;
      delete nextWhere.ownerLast;
    }

    // Evaluations: Subject is stored as owner, not user; map user → owner so the filter applies
    if (location === "evaluations") {
      const userVal = getString(nextWhere.user);
      if (userVal !== undefined && userVal !== null && userVal !== "") {
        nextWhere.owner = userVal;
      }
      delete nextWhere.user;
    }

    if (andConditions.length > 0) {
      if (Array.isArray(nextWhere.AND)) {
        nextWhere.AND = [...nextWhere.AND, ...andConditions];
      } else {
        nextWhere.AND = andConditions;
      }
    }

    const updateWhereFormat = Object.keys(nextWhere).forEach((key) => {
      if (
        key === "dateCompleted" ||
        key === "dateToCompleteBy" ||
        key === "assignmentDate" ||
        key === "startDate"
      ) {
        if (key === "dateToCompleteBy" || key === "dateCompleted") {
          if (!nextWhere?.overdue) {
            nextWhere[key] = {
              gte: nextWhere[key]?.gte,
              lte: nextWhere[key]?.lte,
            };
          }
        } else {
          nextWhere[key] = {
            gte: nextWhere[key]?.gte,
            lte: nextWhere[key]?.lte,
          };
        }
      } else if (
        (key === "firstName" || key === "lastName") &&
        typeof nextWhere[key] === "string" &&
        nextWhere[key] !== ""
      ) {
        nextWhere[key] = { contains: nextWhere[key], mode: "insensitive" };
      } else if (
        (key === "title" || key === "name" || key === "group" || key === "employeeNumber") &&
        typeof nextWhere[key] === "string" &&
        nextWhere[key] !== ""
      ) {
        nextWhere[key] = { contains: nextWhere[key] };
      } else if (key === "supervisorObj" && nextWhere[key] && typeof nextWhere[key] === "object") {
        // Handle nested supervisorObj structure - keep it as is since it's already properly formatted
        // The structure is: { is: { employeeNumber: { contains: value } } }
        // No transformation needed
      } else if (
        key === "overdue" &&
        nextWhere[key] !== "" &&
        nextWhere[key] !== where[key]
      ) {
        if (nextWhere[key] === "Yes") {
          // date is less than today and dateCompleted is null
          nextWhere.dateToCompleteBy = { lt: new Date().toISOString() };
          nextWhere.dateCompleted = { not: { contains: "+" } }; // basically a check for exists
        } else if (nextWhere[key] === "No") {
          // date is greater than today
          nextWhere.dateToCompleteBy = { gte: new Date().toISOString() };
          delete nextWhere.dateCompleted;
        }
      } else if (key === "needsAssignment") {
        const bool = nextWhere[key] === "Yes";
        nextWhere.needsAssignment = { equals: bool };
      } else if (key === "year" && nextWhere[key] !== "" && location === "reviews") {
        // Handle year filter by filtering on ObjectId timestamp (reviews only)
        const year = parseInt(nextWhere[key], 10);
        const startOfYear = new Date(year, 0, 1); // January 1st of that year
        const endOfYear = new Date(year + 1, 0, 1); // January 1st of next year
        
        // Convert dates to ObjectId hex strings for filtering
        const startObjectId = Math.floor(startOfYear.getTime() / 1000).toString(16).padEnd(24, '0');
        const endObjectId = Math.floor(endOfYear.getTime() / 1000).toString(16).padEnd(24, '0');
        
        nextWhere.id = {
          gte: startObjectId,
          lt: endObjectId
        };
        delete nextWhere.year; // Remove the year filter since we're using id filter
      } else if (
        (key === "year" || key === "month") &&
        location === "ppa-assignments"
      ) {
        // Handled below: year/month -> dateToCompleteBy range
      } else if (typeof nextWhere[key] === "string" && nextWhere[key] !== "") {
        nextWhere[key] = { equals: nextWhere[key] };
      }
    });

    // PPA Assignments: year and/or month filter by dateToCompleteBy range
    if (location === "ppa-assignments" && (nextWhere.year || nextWhere.month)) {
      const y = nextWhere.year ? parseInt(nextWhere.year, 10) : new Date().getFullYear();
      const m = nextWhere.month ? parseInt(nextWhere.month, 10) - 1 : 0;
      const start = new Date(y, m, 1);
      const end = nextWhere.month
        ? new Date(y, m + 1, 0, 23, 59, 59, 999)
        : new Date(y + 1, 0, 1);
      nextWhere.dateToCompleteBy = {
        gte: start.toISOString(),
        lte: end.toISOString(),
      };
      delete nextWhere.year;
      delete nextWhere.month;
    }

    const isWhereEqual = equals(where, updateWhereFormat);
    if (!isWhereEqual) {
      // Always preserve base constraints (e.g., page defaults) while allowing overrides
      const finalWhere = { ...baseWhereRef.current, ...nextWhere };
      setWhere(finalWhere);
      setCurrentPage(0);
    }
  };

  const handleFilterComponentClick = () => {
    if (typeof FilterComponent === "function") {
      setModalOpen(
        true,
        <FilterComponent
          inputs={uiWhereRef.current || where}
          baseline={baseWhereRef.current}
          location={location}
          onComplete={handleFilterComplete}
        />
      );
    }
  };

  const handleViewsComponentClick = () => {
    if (typeof ViewsComponent === "function") {
      setModalOpen(true, <ViewsComponent onComplete={handleFilterComplete} />);
    }
  };

  // const handleSearchChange = (e) => {
  //   setSearch(e.target.value);
  // };

  const handleClearRowSelection = () => {
    setRowSelection({});
  };

  // const handleScrollChange = (e) => {
  //   const bottom =
  //     e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;

  //   if (bottom && take < total) {
  //     setTake(take + 20);
  //   }
  // };

  const getOverdueStatus = (dateToCompleteBy, dateCompleted) =>
    dateToCompleteBy &&
      new Date(dateToCompleteBy) < new Date() &&
      !dateCompleted
      ? "Yes"
      : "No";

  const getItemByTableName = (item) => {
    if (name === "all-reviews") {
      return (
        // prob want to move into seperate component ReviewsAccordionItem..
        <AccordionItem
          info={
            getOverdueStatus(item?.dateToCompleteBy, item?.dateCompleted) ===
              "Yes" ? (
              <AssignmentLateIcon />
            ) : null
          }
          key={item?.id}
          title={`${item?.user?.firstName} ${item?.user?.lastName}`}
        >
          <AccordionDetailsItem label="Status" value={item?.status} />
          <AccordionDetailsItem label="Title" value={item?.title} />
          <AccordionDetailsItem label="year" value={item?.year} />
          <AccordionDetailsItem label="Manager" value={item?.manager} />
          <AccordionDetailsItem
            label="Subject"
            value={`${item?.user?.firstName} ${item?.user?.lastName}`}
          />
          <AccordionDetailsItem
            label="Due Date"
            value={
              item?.dateToCompleteBy
                ? format(new Date(item?.dateToCompleteBy), "MM/dd/yyyy")
                : ""
            }
          />
          <AccordionDetailsItem
            label="Overdue"
            value={getOverdueStatus(
              item?.dateToCompleteBy,
              item?.dateCompleted
            )}
          />
          <AccordionDetailsItem
            label="Date Completed"
            value={format(new Date(item?.dateCompleted), "MM/dd/yyyy")}
          />
          <Button
            fullWidth
            color="secondary"
            sx={{ color: theme.palette.common.white }}
            variant="contained"
            onClick={() => onRowClick(item)}
          >
            Details
          </Button>
        </AccordionItem>
      );
    }
    if (name === "all-evals") {
      return (
        <AccordionItem
          info={
            getOverdueStatus(item?.dateToCompleteBy, item?.dateCompleted) ===
              "Yes" ? (
              <AssignmentLateIcon />
            ) : null
          }
          key={item?.id}
          title={item?.owner}
        >
          <AccordionDetailsItem label="Status" value={item?.status} />
          <AccordionDetailsItem label="Title" value={item?.title} />
          <AccordionDetailsItem label="Manager" value={item?.manager} />
          <AccordionDetailsItem label="Writer" value={item?.formAuthor} />
          <AccordionDetailsItem
            label="Subject"
            value={item?.owner}
          />
          <AccordionDetailsItem label="Current Author" value={item?.author} />
          <AccordionDetailsItem
            label="Needs Assignment"
            value={item?.needsAssignment ? "Yes" : "No"}
          />
          <AccordionDetailsItem
            label="Assignment Date"
            value={
              item?.assignmentDate
                ? format(new Date(item?.assignmentDate), "MM/dd/yyyy")
                : ""
            }
          />
          <AccordionDetailsItem
            label="Due Date"
            value={
              item?.dateToCompleteBy
                ? format(new Date(item?.dateToCompleteBy), "MM/dd/yyyy")
                : ""
            }
          />
          <AccordionDetailsItem
            label="Date Completed"
            value={format(new Date(item?.dateCompleted), "MM/dd/yyyy")}
          />
          <AccordionDetailsItem label="Signer" value={(item?.signedDate ? item?.signerName : "")} />
          <AccordionDetailsItem label="Signed Date" value={format(new Date(item?.signedDate), "MM/dd/yyyy")} />
          <AccordionDetailsItem
            label="Overdue"
            value={getOverdueStatus(
              item?.dateToCompleteBy,
              item?.dateCompleted
            )}
          />
          <Button
            fullWidth
            color="secondary"
            sx={{ color: theme.palette.common.white }}
            variant="contained"
            onClick={() => onRowClick(item)}
          >
            Details
          </Button>
        </AccordionItem>
      );
    }
    if (name === "review-cycles") {
      return (
        <AccordionItem key={item?.id} title={item?.name}>
          <AccordionDetailsItem label="Group Cycle" value={item?.groupCycle} />
          <AccordionDetailsItem
            label="Start Date"
            value={format(new Date(item?.startDate), "MM/dd/yyyy")}
          />
        </AccordionItem>
      );
    }
    if (name === "workshop") {
      return (
        <AccordionItem info={item?.year} key={item?.id} title={item?.title}>
          <Button
            fullWidth
            color="secondary"
            sx={{ color: theme.palette.common.white }}
            variant="contained"
            onClick={() => onRowClick(item)}
          >
            Details
          </Button>
        </AccordionItem>
      );
    }
    if (name === "all-goals" || name === "web-editor-goals") {
      return (
        <AccordionItem info={item?.group} key={item?.id} title={`${`${item?.firstName} ${item?.lastName}`}`}>
          <AccordionDetailsItem label="Employee #" value={item?.employeeNumber} />
          <AccordionDetailsItem label="Manager Employee #" value={item?.supervisorObj?.employeeNumber} />
          <Button
            fullWidth
            color="secondary"
            sx={{ color: theme.palette.common.white }}
            variant="contained"
            onClick={() => onRowClick(item)}
          >
            Details
          </Button>
        </AccordionItem>
      );
    }
    if (name === "all-followups" || name === "web-editor-followups") {
      const assignedUsers = item?.followups
        ?.filter(followup => followup.assignedToUser)
        ?.map(followup => `${followup.assignedToUser.firstName} ${followup.assignedToUser.lastName}`)
        ?.filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates
      const assignedTo = assignedUsers?.length > 0 ? assignedUsers.join(", ") : "Not assigned";
      
      return (
        <AccordionItem info={item?.group} key={item?.id || item?.employeeNumber} title={`${item?.firstName} ${item?.lastName}`}>
          <AccordionDetailsItem label="Employee #" value={item?.employeeNumber} />
          <AccordionDetailsItem label="Manager Employee #" value={item?.supervisorObj?.employeeNumber} />
          <AccordionDetailsItem label="Assigned to" value={assignedTo} />
          <Button
            fullWidth
            color="secondary"
            sx={{ color: theme.palette.common.white }}
            variant="contained"
            onClick={() => onRowClick(item)}
          >
            Details
          </Button>
        </AccordionItem>
      );
    }
    if (name === "assigned-followups") {
      return (
        <AccordionItem info={item?.status} key={item?.id} title={item?.title}>
          <AccordionDetailsItem label="Description" value={item?.description} />
          <AccordionDetailsItem label="Partner" value={`${item?.user?.firstName} ${item?.user?.lastName}`} />
          <AccordionDetailsItem label="Partner ID" value={item?.user?.employeeNumber} />
          <AccordionDetailsItem label="Group" value={item?.user?.group} />
          <AccordionDetailsItem label="Created By" value={item?.createdBy} />
          <Button
            fullWidth
            color="secondary"
            sx={{ color: theme.palette.common.white }}
            variant="contained"
            onClick={() => onRowClick(item)}
          >
            Details
          </Button>
        </AccordionItem>
      );
    }
    if (name === "assignment-history") {
      return (
        <AccordionItem info={format(new Date(item?.updatedAt), "MM/dd/yyyy")} key={item?.id} title={item?.projectName}>
          <AccordionDetailsItem label="Project Id" value={item?.projectId} />
          <AccordionDetailsItem label="Updated By" value={item?.updatedByFullName} />
        </AccordionItem>
      );
    }
    return null;
  };

  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  if (!Array.isArray(result) || loading) {
    return <Loader />;
  }

  if (downSm) {
    return (
      <Stack
        alignItems="center"
        direction="column"
        justifyContent="space-between"
        sx={{
          position: "sticky",
          left: 0,
          top: 0,
          margin: 0,
          padding: 0,
          bgcolor: "background.default",
        }}
      >
        <MobileTable
          showLoadingBar
          // handleScrollChange={handleScrollChange}
          headerActions={
            <React.Fragment>
              <Button
                color="primary"
                sx={{ width: "100%" }}
                variant="contained"
                onClick={() => executeQuery()}
              >
                <RefreshIcon
                  sx={{
                    animationName: "spin",
                    animationDuration: "1s",
                    animationIterationCount: "infinite",
                    animationFillMode: "forwards",
                    animationPlayState: loading ? "running" : "paused",
                    "@keyframes spin": {
                      "0%": {
                        transform: "rotate(0deg)",
                      },
                      "100%": {
                        transform: "rotate(360deg)",
                      },
                    },
                  }}
                />
              </Button>
              {name !== "assignment-history" && (
                <Button
                  color="primary"
                  sx={{ width: "100%" }}
                  variant="contained"
                  onClick={() => handleFilterComponentClick()}
                >
                  <FilterAlt />
                </Button>
              )}
            </React.Fragment>
          }
          loading={loading}
          maxHeight="35rem"
          showExportButton={false}
          total={total}
        >
          {loading ? (
            <BoxLoader />
          ) : (
            result?.map((item) => getItemByTableName(item))
          )}
        </MobileTable>
      </Stack>
    );
  }

  return (
    <React.Fragment>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        sx={{
          position: "sticky",
          left: 0,
          top: 0,
          bgcolor: "background.default",
        }}
      >
        <TablePagination
          component="div"
          count={total}
          page={currentPage}
          rowsPerPage={defaultPageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {ActionComponent && (
            <ActionComponent
              executeQuery={executeQuery}
              isSomeRowsSelected={isSomeRowsSelected}
              onClearRowSelection={handleClearRowSelection}
              rowSelection={rowSelection}
            />
          )}
          {FilterComponent && (
            <Button color="primary" onClick={handleFilterComponentClick}>
              Filters
            </Button>
          )}
          {ViewsComponent && (
            <Button color="primary" onClick={handleViewsComponentClick}>
              Views
            </Button>
          )}
          <IconButton color="primary" onClick={() => executeQuery()}>
            <RefreshIcon
              sx={{
                animationName: "spin",
                animationDuration: "1s",
                animationIterationCount: "infinite",
                animationFillMode: "forwards",
                animationPlayState: loading ? "running" : "paused",
                "@keyframes spin": {
                  "0%": {
                    transform: "rotate(0deg)",
                  },
                  "100%": {
                    transform: "rotate(360deg)",
                  },
                },
              }}
            />
          </IconButton>
        </Box>
      </Stack>
      <TableContainer>
        <Box
          sx={isModalTable && { height: "29rem", overflowY: "auto !important" }}
        >
          <Table
            columns={columns}
            data={result}
            fullWidth={fullWidth}
            getIsRowSelected={(row) => !!rowSelection[row?.id] || getIsRowSelected(row)}
            rowSelection={rowSelection}
            sorting={sorting}
            enableRowSelection={enableRowSelection}
            onCellClick={onCellClick}
            onRowClick={onRowClick}
            onRowSelectionChange={handleRowSelectionChange}
            onSortingChange={handleSortingChange}
          />
        </Box>
      </TableContainer>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        sx={{ position: "sticky", left: 0 }}
      >
        <TablePagination
          component="div"
          count={total}
          page={currentPage}
          rowsPerPage={defaultPageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Stack>
    </React.Fragment>
  );
};

DetailedDataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  query: PropTypes.any.isRequired,
  dataExtractor: PropTypes.func,
  totalExtractor: PropTypes.func,
  searchTransformer: PropTypes.func,
  defaultSorting: PropTypes.array,
  onRowClick: PropTypes.func,
  onCellClick: PropTypes.func,
  fetchPolicy: PropTypes.string,
  where: PropTypes.object,
  getIsRowSelected: PropTypes.func,
  enableRowSelection: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  FilterComponent: PropTypes.any,
  ActionComponent: PropTypes.any,
  ViewsComponent: PropTypes.any,
  opVarStorageKey: PropTypes.any,
  location: PropTypes.any,
  fullWidth: PropTypes.bool,
  isModalTable: PropTypes.bool,
  aggregateName: PropTypes.string,
  name: PropTypes.string,
};

DetailedDataTable.defaultProps = {
  defaultSorting: [],
  onRowClick: () => { },
  onCellClick: () => { },
  dataExtractor: () => { },
  totalExtractor: () => { },
  searchTransformer: () => { },
  fetchPolicy: "cache-and-network",
  where: {},
  getIsRowSelected: () => false,
  enableRowSelection: true,
  FilterComponent: null,
  ActionComponent: null,
  ViewsComponent: null,
  opVarStorageKey: null,
  location: "",
  fullWidth: false,
  isModalTable: false,
  aggregateName: "",
  name: "",
};

export default DetailedDataTable;
